import { useState } from 'react';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Mail, MapPin, TrendingUp, CheckCircle2 } from 'lucide-react';

const subscriberSchema = z.object({
  full_name: z.string().trim().min(2, 'Please enter your name').max(100),
  email: z.string().trim().email('Please enter a valid email').max(255),
  zip_code: z
    .string()
    .trim()
    .max(10)
    .regex(/^[0-9]{0,5}$/, 'Enter a valid ZIP')
    .optional()
    .or(z.literal('')),
});

const NewsletterSignup = () => {
  const { toast } = useToast();
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [zip, setZip] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const parsed = subscriberSchema.safeParse({
      full_name: fullName,
      email,
      zip_code: zip,
    });

    if (!parsed.success) {
      toast({
        title: 'Please check your details',
        description: parsed.error.issues[0].message,
        variant: 'destructive',
      });
      return;
    }

    setIsLoading(true);
    try {
      const { error } = await supabase.from('newsletter_subscribers').insert({
        full_name: parsed.data.full_name,
        email: parsed.data.email,
        zip_code: parsed.data.zip_code || null,
        source: 'homepage',
      });

      if (error) {
        if (error.code === '23505') {
          toast({
            title: "You're already on the list",
            description: 'This email is already subscribed. Welcome back!',
          });
          setSuccess(true);
        } else {
          throw error;
        }
      } else {
        setSuccess(true);
        toast({
          title: 'Welcome to the inside track',
          description: "You'll get our next Central Florida market update.",
        });
      }
    } catch (err) {
      toast({
        title: 'Something went wrong',
        description: 'Please try again in a moment.',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <section className="py-20 px-6 bg-brand-warm-50">
      <div className="max-w-5xl mx-auto">
        <div className="bg-white border border-border rounded-2xl shadow-sm overflow-hidden">
          <div className="grid md:grid-cols-2">
            {/* Left: Pitch */}
            <div className="p-8 md:p-12 bg-primary text-primary-foreground flex flex-col justify-center">
              <div className="inline-flex items-center gap-2 bg-white/15 px-3 py-1 rounded-full text-xs font-medium mb-5 w-fit">
                <TrendingUp className="h-3.5 w-3.5" />
                Central Florida Market Insights
              </div>
              <h2 className="text-3xl md:text-4xl font-semibold mb-4 leading-tight">
                The Central Florida market, in your inbox.
              </h2>
              <p className="text-primary-foreground/85 text-base leading-relaxed mb-6">
                One email a month. Real price trends, neighborhood shifts, and savings opportunities
                across Orlando, Tampa, and Miami — written by the REALV3 team.
              </p>
              <ul className="space-y-2.5 text-sm text-primary-foreground/90">
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="h-4 w-4 mt-0.5 flex-shrink-0" />
                  Monthly local price & inventory updates
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="h-4 w-4 mt-0.5 flex-shrink-0" />
                  Neighborhood spotlights & buyer/seller tips
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="h-4 w-4 mt-0.5 flex-shrink-0" />
                  No spam. Unsubscribe anytime.
                </li>
              </ul>
            </div>

            {/* Right: Form */}
            <div className="p-8 md:p-12 flex flex-col justify-center">
              {success ? (
                <div className="text-center py-8">
                  <div className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-primary/10 mb-4">
                    <CheckCircle2 className="h-7 w-7 text-primary" />
                  </div>
                  <h3 className="text-2xl font-semibold text-foreground mb-2">You're in.</h3>
                  <p className="text-muted-foreground">
                    Watch your inbox for the next Central Florida market update.
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label htmlFor="ns-name" className="block text-sm font-medium text-foreground mb-1.5">
                      Your name
                    </label>
                    <Input
                      id="ns-name"
                      type="text"
                      value={fullName}
                      onChange={(e) => setFullName(e.target.value)}
                      placeholder="Jane Smith"
                      maxLength={100}
                      required
                    />
                  </div>

                  <div>
                    <label htmlFor="ns-email" className="block text-sm font-medium text-foreground mb-1.5">
                      Email address
                    </label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="ns-email"
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="you@email.com"
                        className="pl-9"
                        maxLength={255}
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="ns-zip" className="block text-sm font-medium text-foreground mb-1.5">
                      ZIP code <span className="text-muted-foreground font-normal">(optional)</span>
                    </label>
                    <div className="relative">
                      <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="ns-zip"
                        type="text"
                        inputMode="numeric"
                        value={zip}
                        onChange={(e) => setZip(e.target.value.replace(/\D/g, '').slice(0, 5))}
                        placeholder="32801"
                        className="pl-9"
                        maxLength={5}
                      />
                    </div>
                    <p className="text-xs text-muted-foreground mt-1.5">
                      We'll prioritize updates for your area.
                    </p>
                  </div>

                  <Button
                    type="submit"
                    disabled={isLoading}
                    className="w-full bg-accent hover:bg-accent/90 text-accent-foreground font-semibold h-11"
                  >
                    {isLoading ? 'Subscribing…' : 'Get market insights'}
                  </Button>
                  <p className="text-xs text-muted-foreground text-center">
                    By subscribing you agree to receive monthly market emails from REALV3.
                  </p>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default NewsletterSignup;
